/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * Date Formatter - Phase 9 Migration
 * Форматирование дат в различные представления
 */


/**
 * Date Formatter Class
 * Управляет форматированием дат
 */
class DateFormatter {
    constructor() {
        this.initialized = false;
        this.formatCache = new Map();
        this.defaultLocale = 'ru-RU';
        this.defaultTimezone = 'Europe/Moscow';
        this.formats = new Map();
        
        this.initializeFormats();
        
        // Manager initialization logging only in debug mode
    }
    
    /**
     * Инициализация предустановленных форматов
     */
    initializeFormats() {
        // Standard formats
        this.formats.set('iso', {
            format: 'YYYY-MM-DD',
            example: '2024-01-15'
        });
        
        this.formats.set('iso-datetime', {
            format: 'YYYY-MM-DDTHH:mm:ss',
            example: '2024-01-15T14:30:00'
        });
        
        // Russian formats
        this.formats.set('ru-short', {
            format: 'DD.MM.YYYY',
            example: '15.01.2024'
        });
        
        this.formats.set('ru-long', {
            format: 'DD месяц YYYY',
            example: '15 января 2024'
        });
        
        this.formats.set('ru-full', {
            format: 'день недели, DD месяц YYYY',
            example: 'понедельник, 15 января 2024'
        });
        
        // English formats
        this.formats.set('en-short', {
            format: 'MM/DD/YYYY',
            example: '01/15/2024'
        });
        
        this.formats.set('en-long', {
            format: 'Month DD, YYYY',
            example: 'January 15, 2024'
        });
        
        // Relative formats
        this.formats.set('relative', {
            format: 'relative time',
            example: '2 дня назад'
        });
        
        // Time formats
        this.formats.set('time-24', {
            format: 'HH:mm',
            example: '14:30'
        });
        
        this.formats.set('time-12', {
            format: 'hh:mm AM/PM',
            example: '2:30 PM'
        });
    }
    
    /**
     * Основная функция форматирования даты
     * Мигрировано из legacy formatDate
     */
    formatDate(date, format = 'ru-short', options = {}) {
        if (!date) return '';
        
        // Convert to Date object if needed
        if (typeof date === 'string') {
            date = new Date(date);
        }
        
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }
        
        // Check cache
        const cacheKey = `${date.getTime()}-${format}-${JSON.stringify(options)}`;
        if (this.formatCache.has(cacheKey)) {
            return this.formatCache.get(cacheKey);
        }
        
        try {
            let result = '';
            
            // Handle predefined formats
            if (this.formats.has(format)) {
                result = this.formatByType(date, format, options);
            } else {
                // Handle custom format string
                result = this.formatCustom(date, format, options);
            }
            
            // Cache result
            if (result) {
                this.formatCache.set(cacheKey, result);
                
                // Limit cache size
                if (this.formatCache.size > 500) {
                    const firstKey = this.formatCache.keys().next().value;
                    this.formatCache.delete(firstKey);
                }
            }
            
            if (window.HyperLog && result) {
                window.HyperLog.debug('[DateFormatter] Formatted date:', {
                    input: date.toISOString(),
                    format,
                    output: result
                });
            }
            
            return result;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[DateFormatter] Error formatting date:', error);
            }
            return date.toLocaleDateString();
        }
    }
    
    /**
     * Форматирование по предустановленному типу
     */
    formatByType(date, formatType, options) {
        const locale = options.locale || this.defaultLocale;
        
        switch (formatType) {
            case 'iso':
                return this.formatISO(date);
            case 'iso-datetime':
                return this.formatISODateTime(date);
            case 'ru-short':
                return this.formatRussianShort(date);
            case 'ru-long':
                return this.formatRussianLong(date);
            case 'ru-full':
                return this.formatRussianFull(date);
            case 'en-short':
                return this.formatEnglishShort(date);
            case 'en-long':
                return this.formatEnglishLong(date);
            case 'relative':
                return this.formatRelative(date, options);
            case 'time-24':
                return this.formatTime24(date);
            case 'time-12':
                return this.formatTime12(date);
            default:
                return date.toLocaleDateString(locale);
        }
    }
    
    /**
     * ISO форматирование (YYYY-MM-DD)
     */
    formatISO(date) {
        return date.toISOString().split('T')[0];
    }
    
    /**
     * ISO DateTime форматирование
     */
    formatISODateTime(date) {
        return date.toISOString().replace('Z', '');
    }
    
    /**
     * Русский короткий формат (DD.MM.YYYY)
     */
    formatRussianShort(date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }
    
    /**
     * Русский длинный формат (DD месяц YYYY)
     */
    formatRussianLong(date) {
        const day = date.getDate();
        const monthName = this.getRussianMonthName(date.getMonth());
        const year = date.getFullYear();
        return `${day} ${monthName} ${year}`;
    }
    
    /**
     * Русский полный формат (день недели, DD месяц YYYY)
     */
    formatRussianFull(date) {
        const dayName = this.getRussianDayName(date.getDay());
        const longFormat = this.formatRussianLong(date);
        return `${dayName}, ${longFormat}`;
    }
    
    /**
     * Английский короткий формат (MM/DD/YYYY)
     */
    formatEnglishShort(date) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }
    
    /**
     * Английский длинный формат (Month DD, YYYY)
     */
    formatEnglishLong(date) {
        const monthName = this.getEnglishMonthName(date.getMonth());
        const day = date.getDate();
        const year = date.getFullYear();
        return `${monthName} ${day}, ${year}`;
    }
    
    /**
     * Относительное форматирование
     */
    formatRelative(date, options = {}) {
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);
        
        const locale = options.locale || this.defaultLocale;
        
        if (locale.startsWith('ru')) {
            return this.formatRelativeRussian(diffInDays, diffInHours, diffInMinutes);
        } else {
            return this.formatRelativeEnglish(diffInDays, diffInHours, diffInMinutes);
        }
    }
    
    /**
     * Относительное форматирование на русском
     */
    formatRelativeRussian(days, hours, minutes) {
        if (days === 0) {
            if (hours === 0) {
                if (minutes === 0) return 'только что';
                if (minutes === 1) return '1 минуту назад';
                if (minutes < 5) return `${minutes} минуты назад`;
                return `${minutes} минут назад`;
            }
            if (hours === 1) return '1 час назад';
            if (hours < 5) return `${hours} часа назад`;
            return `${hours} часов назад`;
        }
        
        if (days === 1) return 'вчера';
        if (days === 2) return 'позавчера';
        if (days < 7) return `${days} дня назад`;
        
        const weeks = Math.floor(days / 7);
        if (weeks === 1) return '1 неделю назад';
        if (weeks < 4) return `${weeks} недели назад`;
        
        const months = Math.floor(days / 30);
        if (months === 1) return '1 месяц назад';
        if (months < 12) return `${months} месяца назад`;
        
        const years = Math.floor(days / 365);
        if (years === 1) return '1 год назад';
        return `${years} лет назад`;
    }
    
    /**
     * Относительное форматирование на английском
     */
    formatRelativeEnglish(days, hours, minutes) {
        if (days === 0) {
            if (hours === 0) {
                if (minutes === 0) return 'just now';
                if (minutes === 1) return '1 minute ago';
                return `${minutes} minutes ago`;
            }
            if (hours === 1) return '1 hour ago';
            return `${hours} hours ago`;
        }
        
        if (days === 1) return 'yesterday';
        if (days < 7) return `${days} days ago`;
        
        const weeks = Math.floor(days / 7);
        if (weeks === 1) return '1 week ago';
        if (weeks < 4) return `${weeks} weeks ago`;
        
        const months = Math.floor(days / 30);
        if (months === 1) return '1 month ago';
        if (months < 12) return `${months} months ago`;
        
        const years = Math.floor(days / 365);
        if (years === 1) return '1 year ago';
        return `${years} years ago`;
    }
    
    /**
     * 24-часовой формат времени
     */
    formatTime24(date) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    }
    
    /**
     * 12-часовой формат времени
     */
    formatTime12(date) {
        const hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes} ${ampm}`;
    }
    
    /**
     * Пользовательский формат
     */
    formatCustom(date, formatString, options) {
        let result = formatString;
        
        // Replace format tokens
        const tokens = {
            'YYYY': date.getFullYear(),
            'YY': String(date.getFullYear()).slice(-2),
            'MM': String(date.getMonth() + 1).padStart(2, '0'),
            'M': date.getMonth() + 1,
            'DD': String(date.getDate()).padStart(2, '0'),
            'D': date.getDate(),
            'HH': String(date.getHours()).padStart(2, '0'),
            'H': date.getHours(),
            'mm': String(date.getMinutes()).padStart(2, '0'),
            'm': date.getMinutes(),
            'ss': String(date.getSeconds()).padStart(2, '0'),
            's': date.getSeconds()
        };
        
        for (const [token, value] of Object.entries(tokens)) {
            result = result.replace(new RegExp(token, 'g'), value);
        }
        
        return result;
    }
    
    /**
     * Форматирование даты и времени
     * Мигрировано из legacy formatDateTime
     */
    formatDateTime(date, dateFormat = 'ru-short', timeFormat = 'time-24', separator = ' ') {
        if (!date) return '';
        
        const datePart = this.formatDate(date, dateFormat);
        const timePart = this.formatDate(date, timeFormat);
        
        return `${datePart}${separator}${timePart}`;
    }
    
    /**
     * Получение русского названия месяца
     */
    getRussianMonthName(monthIndex) {
        const months = [
            'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
            'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
        ];
        return months[monthIndex] || '';
    }
    
    /**
     * Получение русского названия дня недели
     */
    getRussianDayName(dayIndex) {
        const days = [
            'воскресенье', 'понедельник', 'вторник', 'среда',
            'четверг', 'пятница', 'суббота'
        ];
        return days[dayIndex] || '';
    }
    
    /**
     * Получение английского названия месяца
     */
    getEnglishMonthName(monthIndex) {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[monthIndex] || '';
    }
    
    /**
     * Очистка кеша форматирования
     */
    clearCache() {
        this.formatCache.clear();
        if (window.HyperLog) {
            window.HyperLog.debug('[DateFormatter] Format cache cleared');
        }
    }
    
    /**
     * Получение доступных форматов
     */
    getAvailableFormats() {
        const formats = {};
        for (const [key, value] of this.formats) {
            formats[key] = {
                format: value.format,
                example: value.example
            };
        }
        return formats;
    }
    
    /**
     * Добавление пользовательского формата
     */
    addCustomFormat(name, formatConfig) {
        this.formats.set(name, formatConfig);
        if (window.HyperLog) {
            window.HyperLog.debug('[DateFormatter] Custom format added:', name);
        }
    }
    
    /**
     * Получение статистики форматтера
     */
    getFormatterStats() {
        return {
            initialized: this.initialized,
            cacheSize: this.formatCache.size,
            availableFormats: this.formats.size,
            defaultLocale: this.defaultLocale,
            defaultTimezone: this.defaultTimezone
        };
    }
}

// Создаем singleton instance
const dateFormatter = new DateFormatter();

// Global exports для backward compatibility
window.dateFormatter = dateFormatter;
window.formatDate = dateFormatter.formatDate.bind(dateFormatter);
window.formatDateTime = dateFormatter.formatDateTime.bind(dateFormatter);


// ES Module exports (для модульной системы)
export { dateFormatter };
export const formatDate = dateFormatter.formatDate.bind(dateFormatter);
export const formatDateTime = dateFormatter.formatDateTime.bind(dateFormatter);
